import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import {
  getStateCategoryRestrictions,
  updateStateCategoryRestrictions,
} from "@/api/taxRules.js";
import { getPlans as getCategories } from "@/api/marketplace.js";

const getCatId = (c) => (typeof c === "object" && c?._id) ? c._id : c;
const getCatName = (c, allCategories) => {
  if (typeof c === "object" && c?.name) return c.name;
  const found = allCategories.find((cat) => cat._id === c);
  return found ? found.name : c;
};

function CategoryRestrictionsModal({ isOpen, setIsOpen, state, onSave }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restricted, setRestricted] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !state?.code) return;

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [restrictedData, categoriesData] = await Promise.all([
          getStateCategoryRestrictions(state.code),
          getCategories(),
        ]);
        if (cancelled) return;
        const list = Array.isArray(restrictedData)
          ? restrictedData
          : restrictedData?.categories || [];
        setRestricted(list);
        setAllCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (error) {
        dispatch(addToast({
          message: error.response?.data?.message || "Failed to load category restrictions",
          type: "error",
        }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, state?.code, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      setPickerOpen(false);
      setPickerSearch("");
    }
  }, [isOpen]);

  const restrictedIds = useMemo(() => restricted.map(getCatId), [restricted]);

  const addableCategories = useMemo(() => {
    const available = allCategories.filter((c) => !restrictedIds.includes(c._id));
    if (!pickerSearch.trim()) return available;
    const q = pickerSearch.toLowerCase();
    return available.filter(
      (c) => c.name?.toLowerCase().includes(q) || c.tag?.toLowerCase().includes(q)
    );
  }, [allCategories, restrictedIds, pickerSearch]);

  const handleRemove = (category) => {
    const id = getCatId(category);
    setRestricted((prev) => prev.filter((c) => getCatId(c) !== id));
  };

  const handleAdd = (category) => {
    if (restrictedIds.includes(category._id)) return;
    setRestricted((prev) => [...prev, category]);
    setPickerSearch("");
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const ids = restricted.map(getCatId);
      await updateStateCategoryRestrictions(state.code, ids);
      dispatch(addToast({
        message: "Category restrictions updated",
        type: "success",
      }));
      onSave?.();
      setIsOpen(false);
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || "Failed to update category restrictions",
        type: "error",
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      title={`Restricted Categories${state?.code ? ` — ${state.code}` : ""}`}
      width="2xl"
    >
      <div className="px-5 py-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Loading categories...</p>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                {restricted.length} restricted categor{restricted.length === 1 ? "y" : "ies"}
              </div>
              <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-900/20">
                {restricted.length === 0 ? (
                  <span className="text-sm text-slate-500 dark:text-slate-400 px-1 py-1">
                    No categories restricted.
                  </span>
                ) : (
                  restricted.map((cat) => {
                    const id = getCatId(cat);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 pl-2 pr-1 py-1 text-xs font-medium rounded-full bg-rose-100 text-rose-600 dark:bg-rose-400/10 dark:text-rose-400"
                      >
                        {getCatName(cat, allCategories)}
                        <button
                          type="button"
                          onClick={() => handleRemove(cat)}
                          className="rounded-full hover:bg-rose-200 dark:hover:bg-rose-400/20 w-4 h-4 flex items-center justify-center"
                          aria-label={`Remove ${getCatName(cat, allCategories)}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
            </div>

            <div className="mb-2">
              <button
                type="button"
                onClick={() => setPickerOpen((v) => !v)}
                className="text-sm text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                {pickerOpen ? "− Hide category picker" : "+ Add categories"}
              </button>
            </div>

            {pickerOpen && (
              <div className="mb-4 border border-slate-200 dark:border-slate-700 rounded-md">
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  className="form-input w-full border-0 border-b border-slate-200 dark:border-slate-700 rounded-none rounded-t-md"
                />
                <div className="max-h-60 overflow-y-auto">
                  {addableCategories.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400 text-center">
                      No categories to add.
                    </div>
                  ) : (
                    addableCategories.map((cat) => (
                      <button
                        key={cat._id}
                        type="button"
                        onClick={() => handleAdd(cat)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
                      >
                        <span className="text-slate-800 dark:text-slate-100">{cat.name}</span>
                        {cat.tag && (
                          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">({cat.tag})</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
        <SecondaryButton onClick={() => setIsOpen(false)} disabled={saving}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSave} loading={saving} disabled={saving || loading}>
          Save
        </PrimaryButton>
      </div>
    </ModalBasic>
  );
}

export default CategoryRestrictionsModal;
